import { useCallback, useState } from 'react';

import { FetchService } from '../../../modules/cloud';
import { useService } from '@toeverything/infra';

export interface DiscourseCategory {
  id: number;
  name: string;
  slug: string;
  description: string;
  color: string;
}

export interface PublishResult {
  topicId: number;
  topicUrl: string;
}

interface ErrorResponse {
  message?: string;
  error?: string;
}

export function useDiscoursePublish() {
  const fetchService = useService(FetchService);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async (): Promise<DiscourseCategory[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchService.fetch('/api/discourse/categories', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData: ErrorResponse = await response.json().catch(() => ({}));
        const message =
          errorData.message ||
          errorData.error ||
          `Failed to fetch categories (${response.status})`;
        throw new Error(message);
      }

      const data = await response.json();
      return data.categories || [];
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch categories';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchService]);

  const publishTopic = useCallback(
    async (
      title: string,
      content: string,
      categoryId: number,
      tags?: string[],
      blobs?: Array<{
        key: string;
        fullRef: string;
        ext: string;
        data: string;
        contentType: string;
      }>
    ): Promise<PublishResult> => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchService.fetch('/api/discourse/publish', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title,
            content,
            categoryId,
            tags,
            blobs,
          }),
        });

        if (!response.ok) {
          const errorData: ErrorResponse = await response.json().catch(() => ({}));
          const message =
            errorData.message ||
            errorData.error ||
            `Failed to publish (${response.status})`;
          throw new Error(message);
        }

        const data = await response.json();
        return data;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to publish';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchService]
  );

  return {
    fetchCategories,
    publishTopic,
    loading,
    error,
  };
}
